import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoCargoInfo } from "@/widgets/offer-cargo-cargo-info";
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { AdminContractCreatePageProps } from "../../model";
import { DestinationsInfo } from "./destinations-info";

export const ContractCreatePreview = ({
  deliveryProposalOfferCargoId,
  offerCargoProcess,
  previewValues,
  onSubmitClick,
  onBackToInProgressClick,
}: AdminContractCreatePageProps) => {
  const links = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return undefined;
    }
    const draftOfferCargoId = offerCargoProcess.data.draftOfferCargo.id;
    return [
      { href: "/admin/cargos", label: "荷物一覧" },
      { href: `/admin/cargos/${draftOfferCargoId}`, label: "荷物詳細" },
      {
        href: `/admin/cargos/${draftOfferCargoId}/delivery-proposals/${deliveryProposalOfferCargoId}`,
        label: "配送提案詳細",
      },
      {
        label: "契約新規作成",
      },
    ];
  }, [
    deliveryProposalOfferCargoId,
    offerCargoProcess.status,
    offerCargoProcess.data,
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmitClick = useCallback(async () => {
    if (!previewValues) {
      return;
    }

    setIsSubmitting(true);
    await onSubmitClick();
    setIsSubmitting(false);
  }, [onSubmitClick, previewValues]);

  const deliveryProposalOfferCargo = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return undefined;
    }
    const devlieryProposal =
      offerCargoProcess.data.deliveryProposalOfferCargos.find(
        (cargo) => cargo.id === deliveryProposalOfferCargoId,
      );
    if (!devlieryProposal) {
      throw new Error("Delivery proposal offer cargo not found");
    }
    return devlieryProposal;
  }, [deliveryProposalOfferCargoId, offerCargoProcess]);

  if (!previewValues) {
    return null;
  }
  if (!offerCargoProcess.data || !deliveryProposalOfferCargo) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box>
      <ScrollToTop />
      <BaseBreadCrumb
        links={links ?? []}
        isLoading={!links}
        sx={{ mb: 3, px: 4 }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          契約新規作成
        </Typography>
      </Box>
      <Box
        display="flex"
        mt={4}
        mb={2}
        bgcolor="grey.50"
        minHeight="600px"
        flexDirection={"column"}
        flexGrow={1}
      >
        <Container
          maxWidth="md"
          sx={{
            bgcolor: "white",
            mt: 4,
            mb: 7,
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "16px",
          }}
        >
          <Stack spacing={4} mt={3} mb={10} px={3}>
            <DestinationsInfo
              deliveryProposalOfferCargo={deliveryProposalOfferCargo}
              contractProposalOfferCargo={previewValues}
            />
            <BaseDivider />
            <OfferCargoCargoInfo
              deliveryProposalOfferCargo={deliveryProposalOfferCargo}
              baseOfferCargo={previewValues}
            />
          </Stack>
        </Container>
      </Box>
      <Stack spacing={1} mb={2}>
        <Stack direction="row" spacing={1} justifyContent="center">
          <BaseButton
            LinkComponent={Link}
            variant="outlined"
            color="primary"
            sx={{ width: "96px", fontSize: "14px", fontWeight: 700 }}
            onClick={onBackToInProgressClick}
          >
            修正する
          </BaseButton>
          <BaseButton
            variant="contained"
            color="primary"
            sx={{ width: "240px", fontSize: "14px", fontWeight: 700 }}
            onClick={handleSubmitClick}
            disabled={isSubmitting}
          >
            作成する
          </BaseButton>
        </Stack>
      </Stack>
    </Box>
  );
};
